import { useAppToast } from "@/hooks/useAppToast";
import { useAppSelector } from "@/store";
import { useGenerateTicketMutation } from "@/store/services/booking";
import { useGetUserPurchaseHistoryQuery } from "@/store/services/user";
import { formatShowDateTime, getCinemaHallTimeZone } from "@/utils/showtime";
import {
  AccordionButton,
  AccordionButtonProps,
  AccordionItem,
  AccordionPanel,
  Box,
  BoxProps,
  Button,
  Flex,
  Text,
} from "@chakra-ui/react";
import { DataTable } from "@picovico/ui";
import {
  catchErrorHandler,
  convertTimeTo12HourFormat,
  downloadFile,
} from "@picovico/utils";
import { Row } from "@tanstack/react-table";
import { format } from "date-fns";
import { isEmpty } from "lodash";
import React, { useState } from "react";
import { IconType } from "react-icons";
import { FaAngleUp, FaCloudDownloadAlt } from "react-icons/fa";
import { UserPurchaseHistoryDTO } from "types/api";

interface HistorySectionProps {
  headerBoxProps?: React.CSSProperties;
  accordionButtonProps?: AccordionButtonProps;
  accordionButtonTextProps?: BoxProps;
  accordionButtonIconWrapperProps?: BoxProps;
  accordionButtonIconProps?: IconType;
}

const HistorySection = ({
  headerBoxProps,
  accordionButtonProps,
  accordionButtonTextProps,
  accordionButtonIconWrapperProps,
  accordionButtonIconProps,
  ...rest
}: HistorySectionProps) => {
  const [pagination, setPagination] = useState({ page: 1, page_size: 5 });
  const [isPending, setIsPending] = useState(new Set());
  const { data, isLoading, isFetching } = useGetUserPurchaseHistoryQuery(
    {
      search: "history",
      page_size: pagination.page_size,
      page: pagination.page,
    },
    { refetchOnMountOrArgChange: true },
  );
  const [generateTicket, { isLoading: isGeneratingTicket }] =
    useGenerateTicketMutation({});
  const { success, error: errorToast } = useAppToast();
  const cinemaHalls = useAppSelector((state) => state.config.cinema_halls);

  const columnSchema = [
    {
      id: "movie",
      accessorKey: "movie",
      header: "Movie",
    },
    {
      id: "show_date_time",
      accessorKey: "show_date_time",
      header: "Date",
      cell: ({ row }: { row: Row<UserPurchaseHistoryDTO> }) => {
        return format(row.original.show_date_time, "dd/MM/yyyy");
      },
    },
    {
      id: "time",
      accessorKey: "show_date_time",
      header: "Time",
      cell: ({ row }: { row: Row<UserPurchaseHistoryDTO> }) => {
        const hallTimezone = getCinemaHallTimeZone(
          cinemaHalls,
          row.original.cinema_hall,
        );
        return (
          formatShowDateTime(
            row.original.show_date_time,
            hallTimezone,
            "h:mm a",
          ) ??
          convertTimeTo12HourFormat(
            format(row.original.show_date_time, "HH:mm"),
          )
        );
      },
    },
    {
      id: "screen",
      accessorKey: "screen",
      header: "Screen",
    },
    {
      id: "seats",
      accessorKey: "seats",
      header: "Seats",
    },
    {
      id: "actions",
      accessorKey: "actions",
      header: "Actions",
      cell: ({ row }: { row: Row<UserPurchaseHistoryDTO> }) => {
        const transCode = row.original.transaction_code;
        const isLoadingThis = isPending.has(transCode);
        return (
          <Button
            isLoading={isGeneratingTicket && isLoadingThis}
            isDisabled={isGeneratingTicket && isLoadingThis}
            variant="ghost"
            p={0}
            color={"#0A4A9B"}
            fontSize={"14px"}
            onClick={(e) => {
              e.stopPropagation();
              setIsPending((prev) => new Set([...prev, transCode]));
              generateTicket({
                transaction_code: row.original?.transaction_code,
              })
                .unwrap()
                .then((res: any) => {
                  if ("data" in res && res.data instanceof Blob)
                    downloadFile(res.data, `ticket.pdf`);
                  success("Ticket Downloaded Successfully");
                })
                .catch((err) => {
                  catchErrorHandler(err, errorToast);
                })
                .finally(() => {
                  setIsPending((prev) => {
                    const next = new Set(prev);
                    next.delete(transCode);
                    return next;
                  });
                });
            }}
            rightIcon={<FaCloudDownloadAlt color="#f36f21" />}
          >
            <Text>Tickets</Text>
          </Button>
        );
      },
    },
  ];

  return (
    <AccordionItem border={"none"}>
      {({ isExpanded }) => (
        <>
          <h2 style={{ padding: "30px 32px", ...headerBoxProps }}>
            <AccordionButton
              p={0}
              _hover={{ background: "none" }}
              gap={1}
              {...accordionButtonProps}
            >
              <Box
                as="span"
                textAlign="left"
                fontSize={"30px"}
                textTransform={"uppercase"}
                {...accordionButtonTextProps}
              >
                History{" "}
              </Box>
              <Box
                sx={{
                  background: "primary.500",
                  borderRadius: "6px",
                  padding: "0px 4px",
                }}
                {...accordionButtonIconWrapperProps}
              >
                <FaAngleUp
                  fontSize={"33px"}
                  color="white"
                  style={{
                    transform: isExpanded ? "rotate(0deg)" : "rotate(180deg)",
                  }}
                  {...accordionButtonIconProps}
                />
                {/* <Image
                  src="/img/angleUp.png"
                  w={"25px"}
                  h={"25px"}
                  transform={isExpanded ? "rotate(0deg)" : "rotate(180deg)"}
                /> */}
              </Box>
            </AccordionButton>
          </h2>
          <Box
            sx={{
              background: "linear-gradient(to right, #f36f21, transparent)",
              content: '""',
              display: "block",
              flex: "0 0 100%",
              height: "3px",
              maxWidth: "100%",
            }}
          ></Box>
          <AccordionPanel background={"primary.200"} padding={0}>
            {isEmpty(data?.data.records) ? (
              <Box padding={"32px"}>
                You donot have any tickets for upcoming shows
              </Box>
            ) : (
              <Box
                overflowX="auto"
                overflowY="hidden"
                w={{
                  base: "full",
                  md: "full",
                  xl: "full",
                }}
                maxW="100%"
                p={{ base: 0, md: 4 }}
              >
                {data?.data.records.map((record) => {
                  return (
                    <DataTable
                      columnSchema={columnSchema}
                      data={[record]}
                      isLoading={isLoading}
                      pageCount={data?.data?.total_pages || 0}
                      {...rest}
                    />
                  );
                })}
                {!!data?.data.next && (
                  <Flex justifyContent={"flex-end"}>
                    <Button
                      borderRadius={"50px"}
                      fontSize={"12px"}
                      isLoading={isFetching}
                      size={"sm"}
                      onClick={() =>
                        setPagination((prev) => {
                          return { ...prev, page_size: prev.page_size + 5 };
                        })
                      }
                    >
                      Load More
                    </Button>
                  </Flex>
                )}
              </Box>
            )}
          </AccordionPanel>
        </>
      )}
    </AccordionItem>
  );
};

export default HistorySection;
